// Your routes — and the navigation binding's, which is the part worth knowing.
//
// THIS FILE IS REPLACED when the navigation shell is re-applied. `bottom-tabs`,
// `stack` and `sidebar` produce different routers, so the binding owns it and
// `appfactory spec apply` rewrites it with `overwrite: true`.
//
// It used to claim the opposite — "the factory never replaces it" — which was
// wrong and expensive: an apply silently took a TABS array full of hand-picked
// labels and icons with it, under a message saying four files were written.
// The apply now says loudly when it REPLACES rather than creates.
//
// So: edit it freely, and commit before running `spec apply`. Everything below
// is yours until the shell itself changes.

import { createRouter, createMemoryHistory } from '@ionic/vue-router';

import TabsShell from './pages/TabsShell.vue';

// The screens themselves come from ./product-routes.js, which is generated from
// .appfactory/product-plan.json by `appfactory product apply`.
//
// Two owners, one file each. THIS file is the shell — bottom-tabs, stack and
// sidebar produce different routers, and the binding rewrites it on every
// `spec apply`. The PRODUCT owns which screens exist. Before this split the
// shell hardcoded the worked example's three pages, so re-applying a shell
// silently deleted every screen the app had grown.
import { PRODUCT_ROUTES, DESTINATION_ROUTES, HOME_ROUTE } from './product-routes.js';

/**
 * A product path, made relative to the tab shell.
 *
 * The plan states routes ABSOLUTELY — `/`, `/tasks/:id` — because that is what
 * a screen's route means to everyone except vue-router. A child route with a
 * leading slash is mounted at the ROOT instead of under its parent, so the
 * shell wraps nothing and its outlet stays empty.
 */
const rel = (p) => String(p).replace(/^\/tabs\//, '').replace(/^\//, '');

/**
 * Where the app opens. main.js pushes this.
 *
 * INSIDE the shell, deliberately. This was `HOME_ROUTE` — the raw plan path —
 * and an app whose primary screen is routed at `/` therefore generated
 * `{ path: '/', redirect: '/' }`. vue-router resolves that redirect, arrives at
 * the same route, resolves it again, and the app dies at boot with
 * `RangeError: Maximum call stack size exceeded`.
 *
 * That throw aborts the rest of main.js — before `router.isReady()`, before
 * `mount('#app')`, before `data-ready` — so `#app` stays empty on every route
 * and every store screenshot comes out blank. Nothing else reports it:
 * `npm run build` succeeds and check_app passed 7/7 against an app that
 * rendered nothing at all.
 *
 * A primary screen at `/` is the common case, and what set_plan encourages.
 */
/**
 * Where each screen is MOUNTED in the shell — and why it is not always `rel`.
 *
 * IonTabs decides which tab is active by asking whether the current path
 * STARTS WITH the tab's href. A home screen planned at `/` is mounted at
 * `/tabs/`, which every path in the app starts with — so the first tab stayed
 * highlighted on every screen, whatever you tapped. A destination planned
 * under another (`/cover/confirm` under `/cover`) had the same problem one
 * level down: Confirm lit up Cover.
 *
 * So a DESTINATION whose path is empty, or sits under another destination's
 * path, is mounted at its own id instead (`/tabs/today`, `/tabs/confirm`).
 * Everything else keeps its planned path, so a detail screen still sits under
 * its list (`/tabs/tasks/3` lights up Tasks). Links go by route NAME, so
 * nothing else needs to know.
 */
const DESTINATION_IDS = new Set(DESTINATION_ROUTES.map((r) => r.id));
const PLANNED = new Set(PRODUCT_ROUTES.map((r) => rel(r.path)));
const mount = (r) => {
  const p = rel(r.path);
  if (!DESTINATION_IDS.has(r.id)) return p;
  const nested = DESTINATION_ROUTES.some((d) => d.id !== r.id && rel(d.path) && p.startsWith(`${rel(d.path)}/`));
  if (p && !nested) return p;
  return PLANNED.has(r.id) ? `tab-${r.id}` : r.id;
};

const HOME_SCREEN = PRODUCT_ROUTES.find((r) => r.path === HOME_ROUTE);
export const HOME = `/tabs/${HOME_SCREEN ? mount(HOME_SCREEN) : rel(HOME_ROUTE)}`;

/** Icons a generated tab falls back to, in order. Reorder or replace freely. */
const TAB_ICONS = [homeOutline, listOutline];

// The tabs, in bar order, with the icon each one wears.
//
// `icon` is an IMPORTED ionicon, not a name. `<ion-icon name="home">` fetches
// its SVG from a CDN at runtime, which the app's content-security-policy
// blocks and an offline app could not reach anyway — you get an invisible tab
// bar that works fine on your machine and is blank on a plane.
//
// Every tab is a route. A tab bar is for DESTINATIONS — if your app's main verb
// needs a permanent home, give it a real screen; if it is an action, it belongs
// on the screen it acts on, the way "Add an item" does on Home.
import { homeOutline, listOutline } from 'ionicons/icons';

export const TABS = [
  // DESTINATIONS, not PRIMARY_ROUTES.
  //
  // Exactly one screen is `primary` — it names where the app opens — so a tab
  // bar built from it has exactly one tab, and every other top-level screen is
  // unreachable. A parameterised screen marked primary made it worse: the tab
  // carried `href="/recipe/:recipeId/capture"`, a literal `:` in a path that
  // can never resolve. `destination` is the question a tab bar is asking, and
  // it already excludes detail screens and settings.
  ...DESTINATION_ROUTES.map((r, i) => ({
    name: r.id,
    // The tab's own short label and icon when the product supplies them
    // ("Confirm", not "Approval: Confirm"; a calendar, not a second house).
    label: r.tab ?? r.label,
    // Shell-prefixed, like every other link into the product: a tab pointing at
    // the bare plan path navigates out of the shell and loses the tab bar.
    path: `/tabs/${mount(r)}`,
    // A tab without an icon renders as a label floating in empty space, so
    // there is always one. Change them freely — this array is yours.
    icon: r.icon ?? TAB_ICONS[i % TAB_ICONS.length],
  })),
];

export default createRouter({
  history: createMemoryHistory(),
  routes: [
    { path: '/', redirect: HOME },
    {
      // The tab shell owns its own outlet, so each tab keeps its own
      // navigation stack. Pushing /tabs/items/3 leaves the tab bar in place
      // and the back button working, and switching away and back returns you
      // to the detail screen rather than the top of the list.
      path: '/tabs/',
      component: TabsShell,
      // The empty child is NOT a redirect. Once paths are relative the home
      // screen occupies that slot itself, and sending it onward to a route that
      // resolves back here is the loop described on HOME above.
      children: [
        // `props: true` on every route, so a screen with `:id` in its path gets
        // it as a prop as well as through useRoute(). A detail screen that has
        // to reach for the router to learn which record it is showing is a
        // screen that cannot be tested without one.
        ...PRODUCT_ROUTES.map((r) => ({
          path: mount(r),
          name: r.id,
          component: r.component,
          props: true,
        })),
      ],
    },
  ],
});
